import React, { useState, useEffect } from 'react';
import { Star, CheckCircle, TrendingUp, ShoppingCart, Play, Quote } from 'lucide-react';

interface PricingProps {
  onOrderClick: () => void;
}

const Pricing: React.FC<PricingProps> = ({ onOrderClick }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  
  useEffect(() => {
    setIsVisible(true);
    
    const interval = setInterval(() => {
      setCurrentTestimonial(prev => (prev + 1) % testimonials.length);
    }, 4000);
    
    return () => clearInterval(interval);
  }, []);

  const testimonials = [
    {
      name: "أحمد بن علي",
      location: "تونس العاصمة",
      rating: 5,
      comment: "تركيب جد سهل! في 3 دقائق كانت سيارتي محمية. أنصح بشدة بهذا المنتج الرائع.",
      date: "منذ شهرين",
      videoQuote: "بسيط وفعال جداً",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face",
      verified: true
    },
    {
      name: "فاطمة الغربي",
      location: "صفاقس", 
      rating: 5,
      comment: "اللصوص حاولوا يسرقوا سيارتي بس ما قدروش بسبب Block Pedal. أنا راضية جداً!",
      date: "منذ شهر",
      videoQuote: "سيارتي في أمان تام",
      avatar: "https://images.unsplash.com/photo-1494790108755-2616b612b2c5?w=100&h=100&fit=crop&crop=face",
      verified: true
    },
    {
      name: "محمد التريكي",
      location: "سوسة",
      rating: 5,
      comment: "جودة ممتازة، توصيل سريع. التركيب ما خذش غير دقائق معدودة. مثالي!",
      date: "منذ 3 أسابيع", 
      videoQuote: "تركيب في 3 دقائق",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face",
      verified: true
    },
    {
      name: "ليلى بن صالح",
      location: "المنستير",
      rating: 5,
      comment: "منتج رائع وخدمة عملاء ممتازة. أشعر بالأمان أكثر الآن مع سيارتي.",
      date: "منذ أسبوعين",
      videoQuote: "أشعر بالأمان التام",
      avatar: "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100&h=100&fit=crop&crop=face",
      verified: true
    }
  ];

  const stats = [
    { 
      value: "1500+", 
      label: "عميل راضٍ", 
      icon: "👥",
      color: "text-blue-600 bg-blue-100",
      description: "عبر كامل التراب التونسي"
    },
    { 
      value: "0", 
      label: "سرقة نجحت", 
      icon: "🛡️",
      color: "text-green-600 bg-green-100",
      description: "مع استخدام منتجاتنا"
    },
    { 
      value: "99.9%", 
      label: "معدل النجاح", 
      icon: "⚡",
      color: "text-purple-600 bg-purple-100",
      description: "في منع محاولات السرقة"
    },
    { 
      value: "24/7", 
      label: "دعم فني", 
      icon: "📞",
      color: "text-orange-600 bg-orange-100",
      description: "متاح في أي وقت"
    }
  ];

  const currentTestimonialData = testimonials[currentTestimonial];

  return (
    <section id="testimonials" className="py-16 lg:py-24 bg-gradient-to-br from-red-50 via-white to-orange-50 relative overflow-hidden">
      
      {/* Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-72 h-72 bg-red-200 rounded-full blur-3xl opacity-20 animate-pulse-slow"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-orange-200 rounded-full blur-3xl opacity-20 animate-pulse-slow"></div>
      </div>

      <div className="container mx-auto px-4 lg:px-6 relative z-10">
        
        {/* Section Header */}
        <div className={`text-center mb-16 ${isVisible ? 'animate-fade-in' : 'opacity-0'}`}>
          <div className="inline-flex items-center space-x-2 bg-red-100 text-red-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Star className="w-4 h-4" />
            <span>آراء عملائنا الكرام</span>
          </div>
          
          <h2 className="text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-800 mb-6">
            <span className="gradient-text">عملاؤنا يتحدثون</span>
            <span className="block text-gray-800">عن تجربتهم</span>
          </h2>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            اكتشف لماذا يثق أكثر من 1500 تونسي في Block Pedal لحماية سياراتهم
          </p>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          
          {/* Video Installation Section */}
          <div className="lg:col-span-4 space-y-8">
            <div id="video-section" className={`${isVisible ? 'animate-slide-up' : 'opacity-0'}`}>
              <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">
                شاهد طريقة التركيب
              </h3>
              
              <div className="relative bg-gray-900 rounded-2xl overflow-hidden shadow-2xl hover-lift">
                <div className="aspect-[280/569] w-full max-w-[320px] mx-auto">
                  <iframe 
                    src="https://www.facebook.com/plugins/video.php?height=569&href=https%3A%2F%2Fwww.facebook.com%2Freel%2F3735200216740503%2F&show_text=false&width=280&t=0" 
                    className="w-full h-full"
                    style={{border: 'none'}}
                    scrolling="no" 
                    frameBorder="0" 
                    allowFullScreen={true}
                    allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                  />
                </div>
                
                {/* Video Overlay Info */}
                <div className="absolute bottom-3 left-3 right-3 bg-black bg-opacity-70 rounded-lg p-3 text-white">
                  <div className="flex items-center gap-2 mb-2">
                    <Play className="w-4 h-4" />
                    <span className="font-semibold text-sm">دليل التركيب</span>
                  </div>
                  <p className="text-xs opacity-90">تركيب في 3 دقائق فقط</p>
                </div>
              </div>
              
              {/* Stats overlay */}
              <div className="absolute -top-3 -right-3 bg-red-500 text-white rounded-lg p-3 shadow-lg">
                <div className="text-xl font-bold">3 دقائق</div>
                <div className="text-xs">وقت التركيب</div>
              </div>
            </div>
          </div>
          
          {/* Testimonials Section */}
          <div className="lg:col-span-8 space-y-8">
            
            {/* Featured Testimonial */}
            <div className={`${isVisible ? 'animate-fade-in' : 'opacity-0'}`}>
              <h3 className="text-2xl font-bold text-gray-800 mb-6 text-right">
                ما يقوله عملاؤنا
              </h3>
              
              <div className="card-modern p-8 hover-lift relative overflow-hidden">
                {/* Quote Icon */}
                <Quote className="absolute top-4 right-4 w-12 h-12 text-red-200 opacity-50" />
                
                <div className="relative">
                  {/* Customer Rating */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center space-x-1">
                      {[...Array(currentTestimonialData.rating)].map((_, i) => (
                        <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                      ))}
                    </div>
                    <div className="text-right">
                      <span className="text-gray-500 text-sm">{currentTestimonialData.date}</span>
                      {currentTestimonialData.verified && (
                        <span className="ml-2 bg-blue-100 text-blue-700 px-2 py-1 rounded-full text-xs font-medium">
                          عميل محقق ✓
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Main Testimonial */}
                  <div className="mb-6">
                    <div className="text-xl font-bold text-red-600 mb-3 text-right">
                      "{currentTestimonialData.videoQuote}"
                    </div>
                    <p className="text-gray-600 text-lg leading-relaxed text-right italic">
                      "{currentTestimonialData.comment}"
                    </p>
                  </div>
                  
                  {/* Customer Info */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <img 
                        src={currentTestimonialData.avatar}
                        alt={currentTestimonialData.name}
                        className="w-12 h-12 rounded-full object-cover shadow-md"
                      />
                      <div className="text-right">
                        <div className="font-semibold text-gray-800">{currentTestimonialData.name}</div>
                        <div className="text-gray-500 text-sm">{currentTestimonialData.location}</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Testimonial Navigation */}
              <div className="flex justify-center mt-6 space-x-3">
                {testimonials.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentTestimonial(index)}
                    className={`w-3 h-3 rounded-full transition-all duration-300 ${
                      currentTestimonial === index 
                        ? 'bg-red-500 w-8' 
                        : 'bg-gray-300 hover:bg-gray-400'
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* Mini Testimonials Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {testimonials.slice(0, 4).map((testimonial, index) => (
                <div
                  key={index}
                  className={`card-modern p-4 cursor-pointer transition-all duration-300 hover:shadow-lg ${
                    currentTestimonial === index ? 'ring-2 ring-red-500' : ''
                  } ${isVisible ? 'animate-slide-up' : 'opacity-0'}`}
                  style={{ animationDelay: `${index * 100}ms` }}
                  onClick={() => setCurrentTestimonial(index)}
                >
                  <div className="flex items-start gap-3">
                    <img 
                      src={testimonial.avatar}
                      alt={testimonial.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div className="flex-1 text-right">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex text-yellow-400">
                          {[...Array(testimonial.rating)].map((_, i) => (
                            <Star key={i} className="w-3 h-3 fill-current" />
                          ))}
                        </div>
                        <div className="font-medium text-gray-800 text-sm">{testimonial.name}</div>
                      </div>
                      <p className="text-gray-600 text-xs leading-relaxed line-clamp-2">
                        {testimonial.comment}
                      </p>
                      <div className="text-xs text-gray-500 mt-1">{testimonial.location}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Performance Stats */}
        <div className="mt-20">
          <div className="text-center mb-12">
            <h3 className="text-3xl font-bold text-gray-800 mb-4">أرقام تتحدث عن نفسها</h3>
            <p className="text-xl text-gray-600">إحصائيات حقيقية تؤكد فعالية منتجاتنا</p>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <div
                key={index}
                className={`card-modern p-6 text-center hover-lift group ${isVisible ? 'animate-slide-up' : 'opacity-0'}`}
                style={{ animationDelay: `${index * 150}ms` }}
              >
                <div className={`w-16 h-16 mx-auto mb-4 ${stat.color} rounded-2xl flex items-center justify-center text-2xl group-hover:scale-110 transition-transform duration-300`}>
                  {stat.icon}
                </div>
                <div className="text-3xl lg:text-4xl font-bold text-gray-800 mb-2 group-hover:scale-105 transition-transform duration-300">
                  {stat.value}
                </div>
                <div className="text-lg font-semibold text-gray-700 mb-1">{stat.label}</div>
                <div className="text-sm text-gray-500">{stat.description}</div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};

export default Pricing;